import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useAuthStore } from '../../lib/auth';
import apiClient from '../../lib/api';

export default function ProcessingStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspace, setWorkspace } = useAuthStore();
  const { completeOnboarding, resetOnboarding, workspaceData, contentSources, selectedPlatforms } = useOnboardingStore();
  const [isProcessing, setIsProcessing] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const processingSteps = [
    {
      icon: '⚙️',
      text: t('onboarding.processing.step1'),
    },
    {
      icon: '🤖',
      text: t('onboarding.processing.step2'),
    },
    {
      icon: '✨',
      text: t('onboarding.processing.step3'),
    },
  ];

  useEffect(() => {
    // Don't start processing until workspace is available
    if (!workspace?.id) {
      console.log('Waiting for workspace to be available...');
      // Set a timeout to redirect to login if workspace is not available after 5 seconds
      const timeout = setTimeout(() => {
        console.error('Workspace not found after timeout, redirecting to login');
        setError('Session expired. Please log in again.');
        setIsProcessing(false);
        setTimeout(() => navigate('/login'), 2000);
      }, 5000);

      return () => clearTimeout(timeout);
    }

    console.log('Workspace available, starting onboarding:', workspace);

    const processOnboarding = async () => {
      try {
        // Step 1: Update workspace
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setCurrentStep(0);

        console.log('Sending PATCH request to:', `/workspace/${workspace.id}`);

        await apiClient.patch(`/workspace/${workspace.id}`, {
          companyName: workspaceData.companyName,
          website: workspaceData.website,
          industry: workspaceData.industry,
          description: workspaceData.description,
          country: workspaceData.country,
          phone: workspaceData.phone,
          contentSources: {
            blogUrls: contentSources.blogUrls,
            productUrls: contentSources.productUrls,
          },
          selectedPlatforms,
          onboardingCompleted: true,
        });

        // Step 2: Brand extraction (AI analysis)
        setCurrentStep(1);

        if (workspaceData.website) {
          console.log('Starting brand extraction for:', workspaceData.website);
          try {
            await apiClient.post('/brand/extract-direct', {
              url: workspaceData.website,
            });
            console.log('Brand extraction completed');
          } catch (brandError: any) {
            console.error('Brand extraction failed:', brandError);
            // Don't fail the entire onboarding if brand extraction fails
            // The user can retry it later from the Brand Settings page
          }
        }

        // Step 3: Create content sources
        setCurrentStep(2);

        // Create blog post sources
        if (contentSources.blogUrls && contentSources.blogUrls.length > 0) {
          console.log('Creating blog post sources:', contentSources.blogUrls);
          for (const url of contentSources.blogUrls) {
            try {
              await apiClient.post('/content-sources', {
                type: 'blog',
                url,
                discoverySource: 'manual',
              });
            } catch (sourceError: any) {
              console.error('Failed to create blog source:', url, sourceError);
              // Continue with other sources
            }
          }
        }

        // Create product/service sources
        if (contentSources.productUrls && contentSources.productUrls.length > 0) {
          console.log('Creating product sources:', contentSources.productUrls);
          for (const url of contentSources.productUrls) {
            try {
              await apiClient.post('/content-sources', {
                type: 'product',
                url,
                discoverySource: 'manual',
              });
            } catch (sourceError: any) {
              console.error('Failed to create product source:', url, sourceError);
              // Continue with other sources
            }
          }
        }

        // Update workspace in auth store to reflect completed onboarding
        if (workspace) {
          setWorkspace({ ...workspace, onboardingCompleted: true });
        }

        // Mark onboarding as complete and reset the store (data is now in database)
        completeOnboarding();
        setIsProcessing(false);

        // Reset onboarding store after a delay (so user can see success message)
        setTimeout(() => {
          resetOnboarding();
        }, 1000);
      } catch (err: any) {
        console.error('Onboarding error:', err);
        setError(err.response?.data?.message || 'Failed to complete onboarding');
        setIsProcessing(false);
      }
    };

    processOnboarding();
  }, [workspace, workspaceData, contentSources, selectedPlatforms, completeOnboarding, setWorkspace, resetOnboarding, navigate]);

  const handleGoToDashboard = () => {
    if (workspace && workspace.slug) {
      navigate(`/${workspace.slug}/dashboard`);
    } else {
      navigate('/login');
    }
  };

  const handleRetry = () => {
    setError(null);
    setIsProcessing(true);
    setCurrentStep(0);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
      <div className="card max-w-2xl w-full text-center">
        {error ? (
          <>
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center text-4xl">
                ✕
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl font-bold mb-4 dark:text-gray-100">
              {t('onboarding.processing.errorTitle')}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              {error}
            </p>

            {/* Retry Button */}
            <Button onClick={handleRetry} className="w-full md:w-auto">
              {t('onboarding.processing.retry')} ↻
            </Button>
          </>
        ) : isProcessing ? (
          <>
            {/* Spinner */}
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 border-4 border-gray-200 dark:border-gray-700 border-t-black dark:border-t-gray-300 rounded-full animate-spin"></div>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold mb-4 dark:text-gray-100">
              {t('onboarding.processing.title')}
            </h1>

            {/* Processing Steps */}
            <div className="space-y-4 mb-8">
              {processingSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-4 p-4 border-2 rounded-lg transition-all duration-500 ${
                    index <= currentStep
                      ? 'border-black dark:border-gray-300 bg-white dark:bg-gray-900 opacity-100'
                      : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 opacity-50'
                  }`}
                >
                  <div className="text-3xl">{step.icon}</div>
                  <div className="flex-1 text-left">
                    <p className="font-medium dark:text-gray-100">{step.text}</p>
                  </div>
                  {index <= currentStep && (
                    <div className="text-green-600 dark:text-green-400 text-xl">✓</div>
                  )}
                </div>
              ))}
            </div>

            {/* Info Text */}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('onboarding.processing.waitText')}
            </p>
          </>
        ) : (
          <>
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center text-4xl">
                ✓
              </div>
            </div>

            {/* Success Message */}
            <h1 className="text-3xl font-bold mb-4 dark:text-gray-100">
              {t('onboarding.processing.completeTitle')}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              {t('onboarding.processing.completeSubtitle', {
                companyName: workspaceData.companyName || t('onboarding.processing.yourWorkspace'),
              })}
            </p>

            {/* Dashboard Button */}
            <Button onClick={handleGoToDashboard} className="w-full md:w-auto">
              {t('onboarding.processing.goToDashboard')} →
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
